import React from "react";

import {
  formatMoney,
  formatMoneyFull,
} from "../../../utils/formatters";

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

export default function RiskFinancingCurve({
  data,
  strategy,
  settings,
}) {
  const currency = settings?.currency || "USD";

  const curve = Array.isArray(data?.curve)
    ? data.curve
    : [];

  const deductible = Number(
    strategy?.deductible ?? 0
  );

  const policyLimit = Number(
    strategy?.policy_limit ?? 0
  );

  const limitEnd = deductible + policyLimit;

  const chartData = curve.map((point) => {
    const loss = Number(point?.loss ?? 0);

    const probability =
      Number(
        point?.exceedance_probability ?? 0
      ) * 100;

    return {
      loss,
      probability,
      retained:
        loss <= deductible
          ? probability
          : null,
      transferred:
        loss > deductible && loss <= limitEnd
          ? probability
          : null,
      residual:
        loss > limitEnd
          ? probability
          : null,
    };
  });

  const maxLoss = Number(
    chartData.at(-1)?.loss ?? limitEnd
  );

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <h2 className="text-lg font-semibold text-slate-900">
        Digital Risk Financing Profile
      </h2>

      <p className="text-sm text-slate-500 mt-1">
        How expected annual losses are financed through retention,
        insurance transfer and residual exposure.
      </p>

      <div className="h-96 w-full mt-4">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />

            <XAxis
              dataKey="loss"
              tickFormatter={(value) =>
                formatMoney(value, currency)
              }
              tick={{ fontSize: 12 }}
            />

            <YAxis
              domain={[0, 100]}
              tickFormatter={(value) => `${value}%`}
            />

            <Tooltip
              formatter={(value, name) => [
                `${Number(value).toFixed(2)}%`,
                name,
              ]}
              labelFormatter={(value) =>
                `Loss: ${formatMoneyFull(
                  value,
                  currency,
                  0
                )}`
              }
            />

            <Area
              type="monotone"
              dataKey="retained"
              stroke="#16a34a"
              fill="#bbf7d0"
              connectNulls={false}
              name="Risk Retention"
            />

            <Area
              type="monotone"
              dataKey="transferred"
              stroke="#ca8a04"
              fill="#fef08a"
              connectNulls={false}
              name="Risk Transfer"
            />

            <Area
              type="monotone"
              dataKey="residual"
              stroke="#dc2626"
              fill="#fecaca"
              connectNulls={false}
              name="Residual Exposure"
            />

            {deductible > 0 && (
              <ReferenceLine
                x={deductible}
                stroke="#16a34a"
                strokeDasharray="4 4"
                label={{
                  value: "Deductible",
                  angle: -90,
                  position: "insideTop",
                  fill: "#16a34a",
                  fontSize: 11,
                }}
              />
            )}

            {policyLimit > 0 && (
              <ReferenceLine
                x={limitEnd}
                stroke="#dc2626"
                strokeDasharray="4 4"
                label={{
                  value: "Policy Limit",
                  angle: -90,
                  position: "insideTop",
                  fill: "#dc2626",
                  fontSize: 11,
                }}
              />
            )}

            {deductible > 0 && (
              <ReferenceLine
                x={deductible / 2}
                stroke="transparent"
                label={{
                  value: "Retention",
                  position: "insideTop",
                  fill: "#16a34a",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              />
            )}

            {policyLimit > 0 && (
              <ReferenceLine
                x={(deductible + limitEnd) / 2}
                stroke="transparent"
                label={{
                  value: "Insurance",
                  position: "insideTop",
                  fill: "#ca8a04",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              />
            )}

            {maxLoss > limitEnd && (
              <ReferenceLine
                x={limitEnd + (maxLoss - limitEnd) / 2}
                stroke="transparent"
                label={{
                  value: "Residual",
                  position: "insideTop",
                  fill: "#dc2626",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5 text-sm">
        <LegendItem
          colorClass="bg-green-500"
          title="Company Retained Risk"
          description="Losses financed directly by the organization."
        />

        <LegendItem
          colorClass="bg-yellow-400"
          title="Insurance Transfer"
          description="Losses transferred to the insurer."
        />

        <LegendItem
          colorClass="bg-red-500"
          title="Residual Risk Exposure"
          description="Losses exceeding current policy limits."
        />
      </div>
    </div>
  );
}

function LegendItem({
  colorClass,
  title,
  description,
}) {
  return (
    <div className="flex items-start gap-2">
      <div
        className={`w-4 h-4 rounded mt-1 ${colorClass}`}
      />

      <div>
        <div className="font-semibold">
          {title}
        </div>

        <div className="text-slate-500">
          {description}
        </div>
      </div>
    </div>
  );
}